import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { Query } from 'stores/dataStore';
import { RequestDto, RequestsStore } from 'stores/requests/requests-store';
import { getProperties } from 'utils/object';
import MobileRequestsList from './mobile-requests-list';
import FilterScreen from '../landing-pages/mobile/filters/filterScreen';
import { buildRequestFormViewModel } from 'stores/requests/request-form-store';
import RequestContainer from '../landing-pages/mobile/requests/request-container';
import MobileListSpinner from '../shared/events-and-requests/mobile-list-spinner';
import MobileEventListFooter from '../shared/events-and-requests/mobile-list-footer';
// import PullToRefresh from '../shared/events-and-requests/pull-to-refresh';
import { IdentityService } from 'services/identity-service';
import { container } from 'inversify.config';
import { extractFriendlyIdNumber, isNullOrWhiteSpaces } from 'utils/useful-functions';
import MobileNewRequestView from './mobile-request-wizard';

interface MobileRequestListPageProps extends RouteComponentProps {
  requestStore?: RequestsStore;
}

const ITEMS_PER_PAGE = 10;

const MobileRequestContainer: React.FC<MobileRequestListPageProps> = ({ requestStore, location, match, history }) => {
  const identityService: IdentityService = container.get(IdentityService);
  const currentUserInfo = identityService.getUserInfo();
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const [showActives, setShowActives] = useState<boolean>(true);

  const getFilterStatus = (actives: boolean): any => {
    if (!actives) return { or: [{ Status: 'Accepted' }, { Status: 'Rejected' }] };
    else {
      if (IdentityService.isPlanner(currentUserInfo)) return { or: [{ Status: 'Pending' }] };
      else return { or: [{ Status: 'Draft' }, { Status: 'Pending' }, { Status: 'InProgress' }] };
    }
  };

  const [showFilters, setShowFilters] = useState(false);
  const [requestFilters, setRequestFilters] = useState<any[]>([]);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: any }>({
    isCanceled: [{ isCanceled: false }],
    status: getFilterStatus(true)
  });

  const [requests, setRequests] = useState<RequestDto[]>([]);
  const [selectedItem, setSelectedItem] = useState<RequestDto>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [createRequest, setCreateRequest] = useState<boolean>(false);

  const queryRef = useRef<Query>({
    searchQuery: '',

    orderBy: [{ direction: 'Descending', field: 'FriendlyEventId', useProfile: false }],

    skip: 0,

    take: ITEMS_PER_PAGE
  });

  const load = useCallback(
    async (page: number = 1) => {
      try {
        setLoading(true);

        const activeFiltersCopy = { ...activeFilters };

        // const friendlyId = 4883; //3126; //
        // if (!Number.isNaN(friendlyId)) {
        //   activeFiltersCopy.FriendlyEventId = `contains(cast(FriendlyEventId, 'Edm.String'), '${friendlyId}')`;
        // }

        const filter = [].concat(
          ...getProperties(activeFiltersCopy)
            .filter(({ value }) => !!value)
            .map(({ value }) => value)
        );

        const skip = (page - 1) * ITEMS_PER_PAGE;

        const queryCopy = {
          ...queryRef.current,
          filter,
          take: ITEMS_PER_PAGE,
          skip
        };

        const result = await requestStore.getAllRequestListAsync(queryCopy);
        setTotalItems(result.count || 0);
        setRequests(result.items);
        queryRef.current = queryCopy;
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    },
    [requestStore, activeFilters]
  );

  useEffect(() => {
    const loadPage = async () => {
      await load(currentPage);
    };

    loadPage();
  }, [currentPage, activeFilters, load]);

  if (error) {
    return (
      <>
        <h3>{t('Requests')}</h3>
        <div className="min-h-screen bg-white pb-20">
          <div>Error: {error}</div>
        </div>
      </>
    );
  }

  const toggleFilters = () => {
    const showFiltersAux = !showFilters;
    setShowFilters(showFiltersAux);
  };

  const toggleActives = () => {
    const showActivesAux = !showActives;
    const activeFiltersAux = {
      isCanceled: [{ isCanceled: false }],
      status: getFilterStatus(showActivesAux)
    };
    setShowActives(showActivesAux);
    setActiveFilters(activeFiltersAux);
  };

  const toggleCreateRequest = () => {
    setCreateRequest(true);
  };

  const handleOnBack = () => {
    const activeFiltersAux = {
      isCanceled: [{ isCanceled: false }],
      status: getFilterStatus(showActives)
    };
    setActiveFilters(activeFiltersAux);
    setRequestFilters([]);
  };

  const handleFilterOnSave = (filter: any) => {
    const activeFiltersAux = {
      isCanceled: [{ isCanceled: false }],
      status: getFilterStatus(showActives)
    };

    if (filter.friendlyId) {
      if (isNullOrWhiteSpaces(filter.friendlyId)) {
        delete activeFiltersAux['friendlyId'];
      } else {
        const friendlyId = extractFriendlyIdNumber(filter.friendlyId, 'R');
        if (!Number.isNaN(friendlyId)) {
          activeFiltersAux['friendlyId'] = `contains(cast(FriendlyEventId, 'Edm.String'), '${friendlyId}')`;
        }
      }
    }

    if (filter.eventType) {
      if (!filter.eventType || filter.eventType.length <= 0) {
        delete activeFiltersAux['eventType'];
      } else {
        const filterValue = {};
        filterValue['EventType/OriginalEventTypeId'] = { in: { type: 'guid', value: filter.eventType.map(x => x.id) } };
        activeFiltersAux['eventType'] = filterValue;
      }
    }

    if (filter.title) {
      if (isNullOrWhiteSpaces(filter.title)) delete activeFiltersAux['title'];
      else activeFiltersAux['title'] = { 'tolower(title)': { contains: filter.title.toLowerCase() } };
    }

    if (filter.machinesModel) {
      if (!filter.machinesModel || filter.machinesModel.length <= 0) {
        delete activeFiltersAux['machinesModel'];
      } else {
        activeFiltersAux['machinesModel'] = {
          requestMachines: { any: [{ machineModelId: { in: { type: 'guid', value: filter.machinesModel.map(machine => machine.id) } } }] }
        };
      }
    }

    if (filter.profession) {
      if (!filter.profession || filter.profession.length <= 0) {
        delete activeFiltersAux['profession'];
      } else {
        const filterValue = {};
        filterValue['RoleId'] = { in: { value: filter.profession.map(roles => roles.id), type: 'guid' } };
        activeFiltersAux['profession'] = filterValue;
      }
    }

    if (filter.trainingLevelId) {
      if (!filter.trainingLevelId || !filter.trainingLevelId.id) {
        delete activeFiltersAux['trainingLevelId'];
      } else {
        activeFiltersAux['trainingLevelId'] = { trainingLevelId: { eq: { type: 'guid', value: filter.trainingLevelId.id } } };
      }
    }

    if (filter.requests) {
      if (!filter.requests || filter.requests.length <= 0) {
        delete activeFiltersAux['requests'];
      } else {
        activeFiltersAux['requests'] = {
          requestingLocationId: { in: { type: 'guid', value: filter.requests.map(location => location.id) } }
        };
      }
    }

    if (filter.deliveryMethodId) {
      if (!filter.deliveryMethodId || !filter.deliveryMethodId.id) {
        delete activeFiltersAux['deliveryMethodId'];
      } else {
        activeFiltersAux['deliveryMethodId'] = { deliveryMethodId: { eq: { type: 'guid', value: filter.deliveryMethodId.id } } };
      }
    }

    if (filter.startDate) {
      if (isNullOrWhiteSpaces(filter.startDate)) {
        delete activeFiltersAux['startDate'];
      } else {
        activeFiltersAux['startDate'] = { startDate: { ge: new Date(filter.startDate) } };
      }
    }

    if (filter.endDate) {
      if (isNullOrWhiteSpaces(filter.endDate)) {
        delete activeFiltersAux['endDate'];
      } else {
        activeFiltersAux['endDate'] = { endDate: { le: new Date(filter.endDate) } };
      }
    }

    if (filter.category) {
      if (!filter.category) {
        delete activeFiltersAux['category'];
      } else {
        activeFiltersAux['category'] = { category: { eq: filter.category } };
      }
    }

    if (filter.isMachineRelated) {
      if (!filter.isMachineRelated || !filter.isMachineRelated.id) {
        delete activeFiltersAux['isMachineRelated'];
      } else {
        activeFiltersAux['isMachineRelated'] = { isMachineRelated: { eq: filter.isMachineRelated.id === 'machine' ? true : false } };
      }
    }

    if (filter.priority) {
      if (!filter.priority || filter.priority.length <= 0) {
        delete activeFiltersAux['priority'];
      } else {
        const filterValue = {};
        filterValue['priorityId'] = { in: { value: filter.priority.map(priority => priority.id), type: 'guid' } };
        activeFiltersAux['priority'] = filterValue;
      }
    }

    if (filter.status) {
      // activeFiltersAux['status'] = filter.status;
      if (!filter.status || filter.status.length <= 0) {
        activeFiltersAux['status'] = getFilterStatus(showActives);
      } else {
        const filterValue = {};
        filterValue['status'] = { in: filter.status };
        activeFiltersAux['status'] = filterValue;
      }
    }

    setShowFilters(false);
    setActiveFilters(activeFiltersAux);
    setRequestFilters(filter);
  };

  const handleOnCloseFilters = (value: boolean) => {
    const activeFiltersAux = {
      isCanceled: [{ isCanceled: false }],
      status: getFilterStatus(showActives)
    };
    setActiveFilters(activeFiltersAux);
    setRequestFilters([]);
    if (value) setShowFilters(false);
  };

  const onNewItemClosed = async () => {
    setCreateRequest(false);
    await load(currentPage);
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  return (
    // <PullToRefresh onRefresh={() => window.location.reload()}>
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: '#00b2a9',
          color: 'white',
          paddingTop: '1rem',
          paddingBottom: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          paddingLeft: '12px'
        }}
      >
        <h3>{t('Requests')}</h3>
      </div>
      <div
        ref={listRef}
        className="bg-white"
        style={{
          overflowY: 'auto',

          paddingBottom: '56px'
        }}
      >
        {createRequest ? (
          <MobileNewRequestView onClose={() => onNewItemClosed()} />
        ) : showFilters ? (
          <FilterScreen
            showActiveFilters={showActives}
            values={requestFilters}
            onClose={value => handleOnCloseFilters(value)}
            onSave={e => handleFilterOnSave(e)}
          />
        ) : selectedItem ? (
          <RequestContainer
            selectedItem={buildRequestFormViewModel(selectedItem)}
            onBack={() => setSelectedItem(null)}
            location={location}
            match={match}
            history={history}
          />
        ) : (
          <>
            {loading ? (
              <MobileListSpinner />
            ) : error ? (
              <div className="p-4 text-red-600">{error}</div>
            ) : (
              <MobileRequestsList onRequestClick={request => setSelectedItem(request)} requests={requests} onBack={handleOnBack} />
            )}
            {!loading && (requests || []).length > 0 && (
              <MobileEventListFooter
                currentPage={currentPage}
                onClick={setCurrentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                showActionFilters={true}
                showActiveEvents={showActives}
                entityType={'request'}
                setShowFilters={toggleFilters}
                setShowActives={toggleActives}
                setCreateRequest={toggleCreateRequest}
              />
            )}
          </>
        )}
      </div>
    </>
    // </PullToRefresh>
  );
};

export default connect(['requestStore', RequestsStore])(MobileRequestContainer);
